import React from 'react';
import { motion } from 'framer-motion';
import profile from '../profile.json';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { Profile } from '../types/profile';

const typedProfile = profile as Profile;

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const } }
};

export default function Experience(): React.ReactElement {
    return (
        <section id='experience' className="py-24 lg:py-32">
            <div className="container-main">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    variants={fadeUp}
                    className="mb-16"
                >
                    <div className="section-label">
                        <span>Experience</span>
                    </div>
                    <h2 className="section-title">Where I've worked</h2>
                </motion.div>

                <div className="max-w-3xl space-y-10">
                    {typedProfile['experience'].map((org, idx) => (
                        <motion.article
                            key={org.name}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: idx * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                            className="timeline-item"
                        >
                            <div className="card-glow p-7 lg:p-8">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
                                    <div>
                                        <h3 className="text-xl lg:text-2xl font-semibold text-slate-900 dark:text-white mb-1.5 tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>
                                            {org.role}
                                        </h3>
                                        <a
                                            href={org.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1.5 text-primary-500 hover:text-primary-400 font-medium text-base group transition-colors"
                                        >
                                            {org.name}
                                            <FontAwesomeIcon
                                                icon={faArrowUpRightFromSquare}
                                                className="text-[10px] opacity-0 group-hover:opacity-100 transition-all"
                                            />
                                        </a>
                                    </div>

                                    <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0">
                                        <span className="font-mono text-xs text-[var(--color-text-muted)] tracking-wide">
                                            {org.start} — {org.end === 'Present'
                                                ? <span className="text-primary-500">Present</span>
                                                : org.end
                                            }
                                        </span>
                                        {org.end === 'Present' && (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono tracking-widest uppercase text-primary-500 bg-primary-500/8 border border-primary-500/20">
                                                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
                                                Current
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <ul className="space-y-3.5">
                                    {org.paras.map((para, i) => (
                                        <motion.li
                                            key={i}
                                            initial={{ opacity: 0, x: -8 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: 0.2 + i * 0.07 }}
                                            className="flex gap-3.5"
                                        >
                                            <span className="text-primary-500 mt-1.5 flex-shrink-0 text-xs">▹</span>
                                            <span className="text-[var(--color-text-secondary)] leading-relaxed text-sm lg:text-base">{para}</span>
                                        </motion.li>
                                    ))}
                                </ul>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}
